import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { List, X } from "@phosphor-icons/react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_1cfd506a-8d58-4dee-aebc-bac5c6ae0bd8/artifacts/olw9pyi7_1630458636004.jpg";

export default function Navbar() {
  const { lang, setLang, t } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const links = [
    { to: "/", label: t.nav.home, exact: true },
    { to: "/philosophy", label: t.nav.philosophy },
    { to: "/approach", label: t.nav.approach },
    { to: "/sectors", label: t.nav.sectors },
    { to: "/portfolio", label: t.nav.portfolio },
  ];

    return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-[#FAF9F6] border-b border-[#EBEAE0] shadow-[0_1px_0_rgba(0,0,0,0.02)]"
      data-scrolled={scrolled}
      data-testid="site-navbar"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between h-24">
        <Link
          to="/"
          className="flex items-center gap-3 group"
          data-testid="nav-logo-link"
        >
          <img
            src={LOGO_URL}
            alt="Elephant Equity"
            className="h-12 w-12 sm:h-14 sm:w-14 object-contain"
            style={{ mixBlendMode: "multiply" }}
          />
          <span className="font-display text-2xl sm:text-[1.6rem] tracking-tight text-[#1A1A1A] leading-none">
            Elephant <span className="text-[#A6192E]">Equity</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.exact}
              data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `text-sm tracking-wide link-underline transition-colors ${
                  isActive ? "text-[#A6192E]" : "text-[#1A1A1A] hover:text-[#A6192E]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div
            className="hidden sm:flex text-xs font-mono border border-[#1A1A1A]/30 rounded-sm overflow-hidden"
            data-testid="lang-toggle"
          >
            <button
              onClick={() => setLang("en")}
              className={`px-3 py-1.5 transition-colors ${
                lang === "en" ? "bg-[#1A1A1A] text-white" : "text-[#1A1A1A] hover:bg-[#1A1A1A]/5"
              }`}
              data-testid="lang-en"
            >
              EN
            </button>
            <button
              onClick={() => setLang("de")}
              className={`px-3 py-1.5 transition-colors ${
                lang === "de" ? "bg-[#1A1A1A] text-white" : "text-[#1A1A1A] hover:bg-[#1A1A1A]/5"
              }`}
              data-testid="lang-de"
            >
              DE
            </button>
          </div>

          <Link
            to="/portfolio"
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 bg-[#A6192E] hover:bg-[#8C1527] text-white text-sm font-medium tracking-wide rounded-sm transition-colors"
            data-testid="nav-cta-portfolio"
          >
            {t.cta.seePortfolio}
          </Link>

          <button
            className="lg:hidden p-2 text-[#1A1A1A]"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} weight="regular" /> : <List size={24} weight="regular" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#FAF9F6] border-t border-[#EBEAE0]">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.exact}
                className={({ isActive }) =>
                  `text-base ${isActive ? "text-[#A6192E]" : "text-[#1A1A1A]"}`
                }
                data-testid={`mobile-nav-${l.to.replace("/", "") || "home"}`}
              >
                {l.label}
              </NavLink>
            ))}
            <div className="flex items-center gap-2 pt-2">
              <button
                onClick={() => setLang("en")}
                className={`px-3 py-1 text-xs font-mono border ${
                  lang === "en" ? "bg-[#1A1A1A] text-white" : "border-[#1A1A1A]/30"
                }`}
              >
                EN
              </button>
              <button
                onClick={() => setLang("de")}
                className={`px-3 py-1 text-xs font-mono border ${
                  lang === "de" ? "bg-[#1A1A1A] text-white" : "border-[#1A1A1A]/30"
                }`}
              >
                DE
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
